import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, Search, Package } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_CATEGORIES = [
  'Kablo',
  'LED',
  'Elektrik Malzemeleri',
  'İnşaat Malzemeleri',
  'Boya',
  'Ahşap',
  'Metal',
  'Sigortalar',
  'Diğer',
];

export const Materials = () => {
  const [materials, setMaterials] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    unit: 'metre',
    pricePerUnit: '',
    description: '',
  });

  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = () => {
    const stored = localStorage.getItem('materials');
    if (stored) {
      setMaterials(JSON.parse(stored));
    }
  };

  const saveMaterials = (updatedMaterials) => {
    localStorage.setItem('materials', JSON.stringify(updatedMaterials));
    setMaterials(updatedMaterials);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.category || !formData.pricePerUnit) {
      toast.error('Lütfen gerekli alanları doldurun');
      return;
    }

    const material = {
      id: editingMaterial?.id || Date.now().toString(),
      ...formData,
      pricePerUnit: parseFloat(formData.pricePerUnit),
      createdAt: editingMaterial?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    let updatedMaterials;
    if (editingMaterial) {
      updatedMaterials = materials.map(m => m.id === editingMaterial.id ? material : m);
      toast.success('Malzeme güncellendi');
    } else {
      updatedMaterials = [...materials, material];
      toast.success('Malzeme eklendi');
    }

    saveMaterials(updatedMaterials);
    resetForm();
    setIsDialogOpen(false);
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      name: material.name,
      category: material.category,
      unit: material.unit,
      pricePerUnit: material.pricePerUnit.toString(),
      description: material.description || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Bu malzemeyi silmek istediğinizden emin misiniz?')) {
      const updatedMaterials = materials.filter(m => m.id !== id);
      saveMaterials(updatedMaterials);
      toast.success('Malzeme silindi');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      unit: 'metre',
      pricePerUnit: '',
      description: '',
    });
    setEditingMaterial(null);
  };

  const filteredMaterials = materials.filter(material => {
    const matchesSearch = material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         material.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || material.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(materials.map(m => m.category))];
  const allCategories = [...new Set([...DEFAULT_CATEGORIES, ...categories])];

  return (
    <div className="p-8 animate-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">Malzemeler</h1>
        <p className="text-muted-foreground text-lg">Malzeme kütüphanenizi yönetin</p>
      </div>

      {/* Actions Bar */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <Label htmlFor="search">Ara</Label>
              <div className="relative mt-1.5">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Malzeme adı veya kategori..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="w-full md:w-48">
              <Label htmlFor="filter">Kategori</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger id="filter" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kategoriler</SelectItem>
                  {allCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90">
                  <Plus className="mr-2 h-4 w-4" />
                  Yeni Malzeme
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>
                    {editingMaterial ? 'Malzeme Düzenle' : 'Yeni Malzeme Ekle'}
                  </DialogTitle>
                  <DialogDescription>
                    Malzeme bilgilerini girin. Fiyat birim başına olarak hesaplanacaktır.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="name">Malzeme Adı *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Örn: NYM 3x2.5 Kablo"
                      className="mt-1.5"
                    />
                  </div>

                  <div>
                    <Label htmlFor="category">Kategori *</Label>
                    <Select 
                      value={formData.category} 
                      onValueChange={(value) => setFormData({...formData, category: value})}
                    >
                      <SelectTrigger id="category" className="mt-1.5">
                        <SelectValue placeholder="Kategori seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {allCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="unit">Birim</Label>
                      <Select 
                        value={formData.unit} 
                        onValueChange={(value) => setFormData({...formData, unit: value})}
                      >
                        <SelectTrigger id="unit" className="mt-1.5">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="metre">Metre</SelectItem>
                          <SelectItem value="adet">Adet</SelectItem>
                          <SelectItem value="kg">Kilogram</SelectItem>
                          <SelectItem value="litre">Litre</SelectItem>
                          <SelectItem value="m2">Metrekare</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="price">Birim Fiyat (₺) *</Label>
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        value={formData.pricePerUnit}
                        onChange={(e) => setFormData({...formData, pricePerUnit: e.target.value})}
                        placeholder="0.00"
                        className="mt-1.5"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="description">Açıklama</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Opsiyonel açıklama"
                      className="mt-1.5"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90">
                      {editingMaterial ? 'Güncelle' : 'Ekle'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsDialogOpen(false)}
                      className="flex-1"
                    >
                      İptal
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>

      {/* Materials Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            Malzeme Listesi
          </CardTitle>
          <CardDescription>
            Toplam {filteredMaterials.length} malzeme
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredMaterials.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Henüz malzeme eklenmemiş</p>
              <Button 
                onClick={() => setIsDialogOpen(true)} 
                className="mt-4 bg-primary hover:bg-primary/90"
              >
                İlk Malzemeyi Ekle
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Malzeme Adı</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead>Birim</TableHead>
                    <TableHead className="text-right">Birim Fiyat</TableHead>
                    <TableHead>Açıklama</TableHead>
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMaterials.map((material) => (
                    <TableRow key={material.id}>
                      <TableCell className="font-medium">{material.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{material.category}</Badge>
                      </TableCell>
                      <TableCell>{material.unit}</TableCell>
                      <TableCell className="text-right font-semibold text-primary">
                        ₺{material.pricePerUnit.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {material.description || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(material)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(material.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Materials;